/* eslint-disable ember/no-computed-properties-in-native-classes */
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import { isProductDisabled } from 'garaje/helpers/product-disabled';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

// Checks if a list of roles contains any of the permitted roles
function includesAny(collection: Array<string>, ...values: Array<string>): boolean {
  return isPresent(_intersection(collection, values));
}

export default class DeskAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;

  @reads('state.features') features!: StateService['features'];
  @reads('currentAdmin.roleNames') roles!: CurrentAdminService['roleNames'];

  get canManage(): boolean {
    return this.hasDesksAccess && includesAny(this.roles, GLOBAL_ADMIN);
  }

  get canUsePartialDayBooking(): boolean {
    return (
      Boolean(this.features?.canUsePartialDayBooking) &&
      this.featureFlags.isEnabled('partial-day-booking-web') &&
      this.featureConfig.isEnabled('deskReservations.partialDayBooking') &&
      Boolean(this.state.currentLocationEnabledDesks)
    );
  }

  // Admin can see the desk column in the entry log
  get canSeeEntryLog(): boolean {
    const permissions = [Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ, Permission.DESKS_AMENITY_READ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // Admin can see the desk column in the invite log
  get canSeeInviteLog(): boolean {
    const permissions = [Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ, Permission.DESKS_AMENITY_READ];

    return this.hasDesksAccess && this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // NAVIGATION

  /**
   * Helper to check if user has desks access via subscription or app_desks feature
   */
  get hasDesksAccess(): boolean {
    return this.featureConfig.isEnabled('desks') || !!this.features?.canAccessDesksApplication;
  }

  get canVisit(): boolean {
    // cannot visit if product is disabled
    if (isProductDisabled('desks')) {
      return false;
    }

    return (
      this.canVisitAllDesks ||
      this.canVisitSettings ||
      this.canVisitReservations ||
      this.canVisitLanding ||
      this.canVisitGetStarted
    );
  }

  get canVisitAllDesks(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_DESK_READ,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_AMENITY_UPDATE,
      Permission.DESKS_AMENITY_DELETE,
    ];

    return this.hasDesksAccess && this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canVisitReservations(): boolean {
    const permissions = [Permission.DESKS_RESERVATION_READ, Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ];

    return (
      this.hasDesksAccess &&
      this.authz.hasAllPermissionsAtCurrentLocation(permissions) &&
      this.featureConfig.isEnabled('deskReservations')
    );
  }

  get canVisitSettings(): boolean {
    const permissions = [
      Permission.DESKS_DESK_SETTING_CREATE,
      Permission.DESKS_DESK_SETTING_UPDATE,
      Permission.DESKS_DESK_SETTING_DELETE,
    ];

    return this.hasDesksAccess && this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canVisitLanding(): boolean {
    const permissions = [
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.MAPS_FLOOR_CREATE,
    ];

    return (
      this.authz.hasAllPermissionsAtCurrentLocation(permissions) &&
      (this.canRequestDesksTrial || this.abilities.can('start-workplace-trial billing'))
    );
  }

  get canVisitGetStarted(): boolean {
    const permissions = [
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.MAPS_FLOOR_CREATE,
    ];

    return this.hasDesksAccess && this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // TRIALS

  get canRequestDesksTrial(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_TRIAL_REQUEST_CREATE);
  }

  // LIVE MAP

  get canShowBulkActions(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.MAPS_AREA_MAP_READ,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
      Permission.DESKS_DESK_UPDATE,
      Permission.DESKS_DESK_DELETE,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  get canManageDesk(): boolean {
    const permissions = [
      Permission.MAPS_LIVE_CREATE,
      Permission.MAPS_LIVE_UPDATE,
      Permission.MAPS_LIVE_DELETE,
      Permission.MAPS_FEATURE_CREATE,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_DESK_UPDATE,
      Permission.DESKS_DESK_DELETE,
      Permission.DESKS_AMENITY_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  get canManagePointOfInterest(): boolean {
    const permissions = [
      Permission.MAPS_LIVE_UPDATE,
      Permission.MAPS_FEATURE_CREATE,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  // ALL DESKS

  get canViewFloors(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_READ);
  }

  get canViewFloorDetails(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
      Permission.DESKS_DESK_READ,
      Permission.DESKS_AMENITY_READ,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canManageFloor(): boolean {
    return this.canCreateFloor && this.canEditFloor && this.canDeleteFloor;
  }

  get canCreateFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_CREATE);
  }

  get canEditFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_UPDATE);
  }

  get canDeleteFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_DELETE);
  }

  get canImportDesks(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_CREATE,
      Permission.MAPS_FLOOR_UPDATE,
      Permission.DESKS_DESK_IMPORT,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_AMENITY_UPDATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.DESKS_NEIGHBORHOOD_UPDATE,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canExportDesks(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_DESK_EXPORT,
      Permission.DESKS_AMENITY_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // NEIGHBORHOODS

  get canCreateNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_CREATE)
      : true;
  }

  get canEditNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_UPDATE)
      : true;
  }

  get canDeleteNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_DELETE)
      : true;
  }

  get canUseDeskLending(): boolean {
    if (!this.featureFlags.isEnabled('reverse-hoteling')) return false;

    return this.authz.hasPermissionAtCurrentLocation(
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_REVERSE_HOTELING,
    );
  }
}
